<template>
  <button
    :type="type"
    class="BaseButton"
    :class="theme"
    :size="size"
    :style="style"
    :disabled="disabled"
    @click="handleClick"
  >
    <slot name="icon" />
    <span class="button__text" :style="textColor ? `color: ${textColor}` : ''"><slot /></span>
    <span v-if="count" class="button__count">{{ count }}</span>
  </button>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  interface Props {
    type?: 'button' | 'submit';
    theme?: null | 'alert' | 'info' | 'confirm' | 'primary' | 'black';
    size?: 'small' | 'medium' | 'large';
    textColor?: string;
    count?: number;
    flex?: number;
    disabled?: boolean;
  }

  const emits = defineEmits(['buttonClick']);
  const props = withDefaults(defineProps<Props>(), {
    type: 'button',
    theme: null,
    size: 'large',
    textColor: '',
    count: undefined,
    disabled: false,
  });

  const style = computed(() => {
    if (props.flex) {
      return `flex: ${props.flex};`;
    }
    return '';
  });

  const handleClick = () => {
    emits('buttonClick');
  };
</script>

<style lang="scss">
  @use '@/assets/scss/variables' as vars;

  .BaseButton {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.7463vw;
    width: 100%;
    height: 6.7164vw;
    border-radius: 0.5224vw;
    background: var(--brand-primary);
    border: none;
    @media (max-width: vars.$sm) {
      height: 60px;
    }
    .button__text {
      font-size: 2.0896vw;
      font-weight: 500;
      color: #fff;
      @media (max-width: vars.$sm) {
        font-size: 18px;
      }
    }
    svg + .button__text,
    img + .button__text {
      margin-left: 0.7463vw;
    }
    svg,
    img {
      width: 2.462vw;
      @media (max-width: vars.$sm) {
        width: 24px;
      }
    }
    &.outline {
      background: #fff;
      border: 0.0746vw solid var(--brand-primary);
      .button__text {
        color: var(--brand-primary);
      }
      svg {
        width: 2.308vw;
        @media (max-width: vars.$sm) {
          width: 24px;
        }
        path {
          fill: var(--brand-primary);
        }
      }
    }
    &[size='small'] {
      height: 2.9851vw;
      .button__text {
        font-size: 1.2687vw;
      }
    }
    &[size='medium'] {
      height: 5vw;
      .button__text {
        font-size: 1.7969vw;
      }
    }
    &[disabled] {
      cursor: not-allowed;
      opacity: 0.5;
    }
    &.alert {
      border: 1px solid #d7201a;
      background: #fff6f6;
      .button__text {
        color: #d7201a;
      }
    }
    &.confirm {
      border: 1px solid #0e566c;
      background: #f4f7fc;
      .button__text {
        color: #0e566c;
      }
    }
    &.info {
      border: 1px solid #1e561f;
      background: #fcfff5;
      .button__text {
        color: #1e561f;
      }
    }
    &.secondary {
      background: #6c757d;
    }
    &.black {
      background-color: #000;
      color: var(--point-color-secondary);
    }
    .button__count {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.9851vw;
      height: 2.9851vw;
      font-size: 1.8657vw;
      font-weight: 700;
      background-color: var(--brand-secondary);
      color: var(--point-color-primary);
      border-radius: 50%;
      @media (max-width: vars.$sm) {
        width: 24px;
        height: 24px;
        font-size: 18px;
      }
    }
  }
</style>
